import { type FocusEvent, type Key, useCallback, useDeferredValue, useEffect, useMemo, useRef } from 'react';
import { sendTrackSiteSearchEvent } from '@pkgs/components/utils/tracking';
import { mapSearchStateToSearchListingsFilters } from '@pkgs/components/utils/mapFilterFields';
import { useMapVisibilityStore } from '@pkgs/components/providers/MapVisibilityStoreProvider';
import { useRecentSearchStorage } from '@pkgs/components/hooks/useRecentSearchStorage';
import { useAutoSuggestOptionGroups } from '@pkgs/components/hooks/useAutoSuggestOptionGroups';
import { useAppContext } from '@pkgs/components/hooks/useAppContext';
import { useAutoCompleteStore } from '@pkgs/components/hooks/stores/useAutoCompleteStore';
import { useSearchStore } from '@pkgs/components/hooks/stores/search/useSearchStore';
import { SearchMode } from '@pkgs/components/hooks/stores/search/slices/searchModeSlice';
import { useAutoCompleteQuery } from '@pkgs/components/hooks/queries/useAutoCompleteQuery';
import { type AddressTypeDto } from '@ev-tech/search-modules-api';

import { isSupportedAddressType } from './isSupportedAddressType';
import { AutoCompleteOption } from './AutoCompleteOption';
import { AutoCompleteComboBox } from './AutoCompleteComboBox';

type Props = {
  initialLocation: string;
  onSelectionChange?: (selected: AutoSuggestOption) => void;
  className?: string;
  'data-testid'?: string;
};

type AutoSuggestPropertyOption = {
  id: string;
  title: string;
  description: string;
  link: string;
  isProperty: true;
  isRecentSearch?: boolean;
};

type AutoSuggestLocationOption = {
  id: string;
  title: string;
  types: AddressTypeDto[];
  isProperty: false;
  isRecentSearch?: boolean;
};

export type AutoSuggestOption = AutoSuggestPropertyOption | AutoSuggestLocationOption;

const MIN_CHARACTERS_FOR_SUGGESTIONS = 3;

export const AutoComplete = ({ initialLocation, onSelectionChange, className, 'data-testid': dataTestId }: Props) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const { t, options: appOptions, countryCode, tracking } = useAppContext();

  // use global store to share the input across filter-bar and advanced filter autosuggest
  // to avoid syncing issues and redundant suggest sessions.
  const inputText = useAutoCompleteStore((state) => state.inputText);
  const setInputText = useAutoCompleteStore((state) => state.setInputText);
  const typedInText = useAutoCompleteStore((state) => state.typedInText);
  const setTypedInText = useAutoCompleteStore((state) => state.setTypedInText);
  const setNumberOfAutoCompleteOptions = useAutoCompleteStore((state) => state.setNumberOfAutoCompleteOptions);
  const setAutoCompleteOptionIndex = useAutoCompleteStore((state) => state.setAutoCompleteOptionIndex);
  const setAutoCompleteOptionCategory = useAutoCompleteStore((state) => state.setAutoCompleteOptionCategory);
  const page = useSearchStore((state) => state.page);
  const getStateSnapshot = useSearchStore((state) => state.getStateSnapshot);
  const isMapToggledOn = useMapVisibilityStore((state) => state.isMapActive);

  const shouldShowSuggestions = useAutoCompleteStore((state) => state.shouldShowSuggestions);
  const setShouldShowSuggestions = useAutoCompleteStore((state) => state.setShouldShowSuggestions);

  const { saveToRecentSearches, searchByTitle, recentSearches: savedAutosuggest } = useRecentSearchStorage();

  // Ensure that the input field is filled with the initial location on server side rendering
  const text = useMemo(() => inputText ?? initialLocation, [initialLocation, inputText]);

  // Use deferred value to improve INP by deferring expensive operations during rapid input
  // This prevents the UI from blocking on every keystroke, especially when deleting or typing quickly
  const deferredText = useDeferredValue(text);

  const { data, isFetching } = useAutoCompleteQuery(deferredText, initialLocation);

  useEffect(() => {
    setInputText(initialLocation);
  }, [initialLocation, setInputText]);

  const handleInputBlur = (_e?: FocusEvent<HTMLInputElement>) => {
    if (!inputText) {
      setInputText(initialLocation);
    }
  };

  const searchSavedAutosuggest = useMemo(() => searchByTitle(deferredText), [deferredText, searchByTitle]);

  const options = deferredText.length >= MIN_CHARACTERS_FOR_SUGGESTIONS && shouldShowSuggestions ? data : [];

  const isLocationNotFound =
    options.length === 0 &&
    deferredText.length >= MIN_CHARACTERS_FOR_SUGGESTIONS &&
    deferredText !== initialLocation &&
    shouldShowSuggestions &&
    !isFetching;

  const { apiSuggestions, recentSearches } = useAutoSuggestOptionGroups(searchSavedAutosuggest, savedAutosuggest, options);

  const allOptions = useMemo(() => [...recentSearches.items, ...apiSuggestions.items], [apiSuggestions, recentSearches]);

  const handleSelectionChange = useCallback(
    async (selection: Key | null) => {
      const option = allOptions.find((suggestion) => suggestion.id === selection);
      if (!option) return;

      const numberOfAutoCompleteOptions = allOptions.length;
      const autoCompleteOptionIndex = allOptions.indexOf(option) + 1;
      setNumberOfAutoCompleteOptions(numberOfAutoCompleteOptions);
      setAutoCompleteOptionIndex(autoCompleteOptionIndex);

      // Set the category for tracking
      const placeType = option.isProperty ? undefined : option.types.find(isSupportedAddressType);
      let category = '';
      if (option.isRecentSearch) {
        category = 'recent-search_';
      }
      if (option.isProperty) {
        category += 'expose';
      } else {
        category += placeType;
      }
      setAutoCompleteOptionCategory(category);

      saveToRecentSearches(option);
      setInputText(option.title);

      if (option.isProperty) {
        const stateSnapshot = getStateSnapshot();

        sendTrackSiteSearchEvent({
          filters: mapSearchStateToSearchListingsFilters(stateSnapshot),
          options: {
            language: appOptions.language,
            currency: appOptions.currency,
            measurementSystem: appOptions.measurementSystem,
            pageSize: appOptions.pageSize,
            page,
          },
          inputText: option.title,
          typedInText,
          numberOfAutoCompleteOptions: numberOfAutoCompleteOptions,
          autoCompleteOptionIndex: autoCompleteOptionIndex,
          tracking,
          countryCode,
          isMapToggledOn,
          autoCompleteOptionCategory: category,
          searchMode: stateSnapshot.searchMode ?? SearchMode.CLASSIC,
          sortingOptionId: stateSnapshot.sortingOptionId,
        });

        // fallback navigation for IOS and safari since clicking the link does not work for ios and safari
        window.location.href = option.link;
      } else {
        onSelectionChange?.(option);
        setShouldShowSuggestions(false);

        await Promise.resolve();
        inputRef.current?.blur();
      }
    },
    [
      allOptions,
      setNumberOfAutoCompleteOptions,
      setAutoCompleteOptionIndex,
      setAutoCompleteOptionCategory,
      saveToRecentSearches,
      setInputText,
      getStateSnapshot,
      appOptions.language,
      appOptions.currency,
      appOptions.measurementSystem,
      appOptions.pageSize,
      page,
      typedInText,
      tracking,
      countryCode,
      isMapToggledOn,
      onSelectionChange,
      setShouldShowSuggestions,
    ],
  );

  const handleInputChange = useCallback(
    (value: string) => {
      setShouldShowSuggestions(true);
      setInputText(value);
      setTypedInText(value);
    },
    [setInputText, setShouldShowSuggestions, setTypedInText],
  );

  return (
    <AutoCompleteComboBox
      className={className}
      inputValue={text}
      onInputChange={handleInputChange}
      autoSuggestOptionGroups={[recentSearches, apiSuggestions]}
      inputRef={inputRef}
      onBlur={handleInputBlur}
      isLocationNotFound={isLocationNotFound}
      isFetching={isFetching}
      onSelectionChange={handleSelectionChange}
      label={t('search.searchbar.locationInput.label')}
      data-testid={dataTestId}
    >
      {(item) => (
        <AutoCompleteOption
          key={item.id}
          option={item}
          data-testid={`search-components_autocomplete_popover_option-${allOptions.indexOf(item)}`}
        />
      )}
    </AutoCompleteComboBox>
  );
};
